import { useCallback } from 'react';

import { useMessagingDeps } from '../provider/use-messaging-deps';
import { MessageEnvelope, MessagingProviderName } from '../types/messaging';

// Ported from register's util/hooks/messaging/useMessageLogging.ts.
// Changes: logger + metrics now come from the DI seam (useMessagingDeps);
// register's `customEventKeys.messaging[provider].messageReceipt` constants are
// inlined here since they're messaging-specific.
const messageReceiptKeys: Record<MessagingProviderName, string> = {
  [MessagingProviderName.Ably]: 'ably-message-receipt',
  [MessagingProviderName.Pusher]: 'pusher-message-receipt',
};

export function useOnChannelMessage(
  channelMessageCallback: (eventName: string, message: string | object) => void,
  provider: MessagingProviderName,
) {
  const { logger, metrics } = useMessagingDeps();

  const onChannelMessage = useCallback(
    (eventName: string | undefined, message: string) => {
      // We don't care to handle pusher internal events here, just continue listening
      if (eventName === undefined || eventName.includes('pusher:') || eventName.includes('pusher_internal:')) {
        return;
      }

      let deserializedMessage: MessageEnvelope | undefined;

      try {
        deserializedMessage = JSON.parse(message);
      } catch (ex: any) {
        metrics.addCount('dpos.deserialization-error', {
          logData: {
            provider,
            message_name: eventName,
            message,
          },
        });
        logger.error(`${provider} message failed to deserialize`, { Exception: ex, SerializedMessage: message });
      }

      /*
       * Unfortunately, there does not appear to be any real type safety available for JSON.parse out of the box
       * Using these if blocks to assert that:
       * 1. the message is populated with some object -> if not, return without handling message
       * 2. the message is in fact a MessageEnvelope -> if not, route the message through callback as is, without logging (this indicates the new pusher/ably client are not yet in use)
       */
      if (deserializedMessage === undefined) {
        return;
      }

      if (deserializedMessage.Data === undefined) {
        channelMessageCallback(eventName, message);
        return;
      }

      metrics.addCount('dpos.message-received', {
        logData: {
          provider,
          message_name: eventName,
          message,
        },
      });
      logger.debug<MessageEnvelope>(`${provider} message received`, {
        ...deserializedMessage,
        key: messageReceiptKeys[provider],
      });
      channelMessageCallback(eventName, deserializedMessage.Data);
    },
    [channelMessageCallback, logger, metrics, provider],
  );

  return onChannelMessage;
}
